import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AdvisorAbsence, AdvisorAbsenceRequest } from '../model';

/**
 * Absences (Abwesenheitsnotiz) of the logged in advisor. During an absence the clients see the
 * note in the chat and get it once per chat as automatic reply.
 */
@Injectable({
  providedIn: 'root',
})
export class AdvisorAbsenceService {
  private readonly baseUrl = `${environment.apiEndpoint}/account/advisor/self/absence`;

  private readonly activeAbsenceSignal = signal<AdvisorAbsence | null>(null);
  /** The absence running today, null if none - drives the palm badge on the header avatar (FIN-7415). */
  public readonly activeAbsence = this.activeAbsenceSignal.asReadonly();

  constructor(private http: HttpClient) {}

  /** Reloads the absences to update {@link activeAbsence}, e.g. once the header is shown. */
  public refreshActiveAbsence(): void {
    this.getAbsences().subscribe({
      error: (): void => this.activeAbsenceSignal.set(null),
    });
  }

  /** All absences, the latest start date first. */
  public getAbsences(): Observable<AdvisorAbsence[]> {
    return this.http
      .get<AdvisorAbsence[]>(this.baseUrl)
      .pipe(
        tap((absences: AdvisorAbsence[]): void =>
          this.activeAbsenceSignal.set(absences.find((absence) => absence.status === 'ACTIVE') ?? null),
        ),
      );
  }

  public createAbsence(request: AdvisorAbsenceRequest): Observable<AdvisorAbsence> {
    return this.http.post<AdvisorAbsence>(this.baseUrl, request).pipe(tap((): void => this.refreshActiveAbsence()));
  }

  public updateAbsence(id: number, request: AdvisorAbsenceRequest): Observable<AdvisorAbsence> {
    return this.http
      .put<AdvisorAbsence>(`${this.baseUrl}/${id}`, request)
      .pipe(tap((): void => this.refreshActiveAbsence()));
  }

  public deleteAbsence(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(tap((): void => this.refreshActiveAbsence()));
  }
}
